import { Browser } from "@capacitor/browser";
import { yupResolver } from "@hookform/resolvers/yup";
import { mdiArrowRight, mdiLoading } from "@mdi/js";
import Icon from "@mdi/react";
import clsx from "clsx";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { bool, object, ref, string } from "yup";

import Button from "components/Button";
import Checkbox from "components/Checkbox";
import Input from "components/Input";
import Radio from "components/Radio";
import Select from "components/Select";
import { routes } from "constants/routes";
import { LANGUAGE_LIST, TERMS_OF_SERVICE } from "constants/signup";
import { RESPONSE_STATUS } from "constants/status";
import useDeviceType from "hooks/useDeviceType";
import type { SignUpUserFormData } from "models/User";
import { FieldError } from "react-hook-form/dist/types/errors";
import { checkEmailExistAPI } from "services/api/user";
import { AppDispatch } from "store";
import { handleLoading } from "store/global/loader";
import styles from "./style.module.scss";
import { useAnalytics } from "hooks/useAnalytics";
import useClarityTracking from "hooks/useClarityTracking";
import useHubspot from "hooks/useHubspot";

const basicFormSchema = object<Partial<SignUpUserFormData>>({
  username: string().required("This field is required"),
  email: string()
    .required("This field is required")
    .email("This field value must be a valid email"),
  password: string()
    .required("This field is required")
    .min(8, "Password must be at least 8 characters"),
  confirmPassword: string()
    .oneOf([ref("password")], "Passwords must match")
    .required("This field is required"),
  agreement: bool().oneOf([true], "You must accept the terms of service")
});

interface IProps {
  basicFormData?: SignUpUserFormData;
  setFormDataCallback: (data: SignUpUserFormData, isValid?: boolean) => void;
  setStepCallback: (step: number) => void;
}

export default function UserSection({
  basicFormData,
  setFormDataCallback,
  setStepCallback
}: Readonly<IProps>) {
  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    formState: { isValid, errors, isSubmitting },
    control,
    watch,
    setValue,
    getValues
  } = useForm<Partial<SignUpUserFormData>>({
    resolver: yupResolver(basicFormSchema),
    defaultValues: { ...basicFormData, language: basicFormData?.language ?? LANGUAGE_LIST[0] }
  });
  const { isDesktop } = useDeviceType();
  const dispatch = useDispatch<AppDispatch>();
  const [emailError, setEmailError] = useState<FieldError>();
  const { trackAllEvents } = useAnalytics();
  const { identifyAnonymousUser } = useClarityTracking();
  const { identifyUser } = useHubspot();

  const onSubmit = async (data: SignUpUserFormData) => {
    if (isValid && emailError === undefined) {
      setFormDataCallback(data, isValid);
      setStepCallback(1);
      trackAllEvents("auth_signup_next_page1");
    }
  };

  const checkEmailExisting = async (email: string) => {
    dispatch(handleLoading(true));
    const {
      data: { status, message }
    } = await checkEmailExistAPI(email).finally(() => dispatch(handleLoading(false)));
    if (status === RESPONSE_STATUS.FAILURE) {
      setEmailError({ type: "custom", message });
      setError("email", { type: "custom", message });
    } else if (status === RESPONSE_STATUS.SUCCESS) {
      setEmailError(undefined);
      clearErrors("email");
      const username = getValues("username");
      identifyUser(email, { name: username });
      identifyAnonymousUser(email, username ?? undefined);
    }
  };
  const selectedLang = watch("language");

  const goToTCPage = async (event: any) => {
    if (!isDesktop) {
      event.preventDefault();
      await Browser.open({ url: TERMS_OF_SERVICE });
    }
  };

  return (
    <form className={styles.basicForm} onSubmit={handleSubmit(onSubmit)}>
      <Input
        name="username"
        register={register}
        error={errors.username}
        placeholder="Name"
        data-cy="signup-username-input"
        {...(isDesktop && { label: "Name" })}
        labelClass="desktop:!text-base desktop:!font-medium"
      />

      <Input
        name="email"
        type="email"
        register={register}
        error={errors.email ?? emailError}
        onBlur={async (event) => checkEmailExisting(event.target.value)}
        placeholder="Email ID*"
        data-cy="signup-email-input"
        {...(isDesktop && { label: "Email Address" })}
        labelClass="desktop:!text-base desktop:!font-medium"
        inputClass="lowercase"
        autoComplete="username"
      />

      <Input
        name="password"
        register={register}
        placeholder="Password*"
        error={errors.password}
        data-cy="signup-password-input"
        {...(isDesktop && { label: "Create Password" })}
        labelClass="desktop:!text-base desktop:!font-medium"
        autoComplete="current-password"
        isPasswordIcon
      />

      <Input
        name="confirmPassword"
        register={register}
        placeholder="Confirm Password*"
        error={errors.confirmPassword}
        data-cy="signup-confirm-password-input"
        {...(isDesktop && { label: "Confirm Password" })}
        labelClass="desktop:!text-base desktop:!font-medium"
        isPasswordIcon
      />

      {!isDesktop ? (
        <Controller
          name="language"
          control={control}
          render={({ field: { value, onChange } }) => (
            <Select
              value={value}
              onChange={onChange}
              options={LANGUAGE_LIST}
              label="Language Preference"
              data-cy="signup-language-select"
            />
          )}
        />
      ) : (
        <div className={clsx(styles.languagePreference, "mobile:!hidden")}>
          <span className="text-sm text-tertiary">Language preference</span>

          <div className="flex gap-4">
            <Radio
              label="English"
              name="language"
              value="en"
              checked={selectedLang?.value === "en"}
              onChange={(event) =>
                setValue(
                  "language",
                  LANGUAGE_LIST.find((l) => l.value === event.target.value)
                )
              }
            />
            <Radio
              label="Spanish"
              name="language"
              value="es"
              checked={selectedLang?.value === "es"}
              onChange={(event) =>
                setValue(
                  "language",
                  LANGUAGE_LIST.find((l) => l.value === event.target.value)
                )
              }
            />
          </div>
        </div>
      )}

      <Checkbox
        name="agreement"
        register={register}
        error={errors.agreement}
        wrapperClass="mobile:mt-16"
        data-cy="signup-agreement-checkbox">
        I agree to the HaulPay{" "}
        <a
          href={TERMS_OF_SERVICE}
          target="blank"
          onClick={goToTCPage}
          className="underline text-primary">
          Terms of Service
        </a>
      </Checkbox>

      <Button
        type="submit"
        size="lg"
        suffixNode={
          !isDesktop && (
            <span className={styles.arrowRightIcon}>
              <Icon path={mdiArrowRight} className="m-auto" />
            </span>
          )
        }
        className="desktop:!px-16 desktop:w-fit"
        data-cy="signup-submit-button"
        disabled={isSubmitting}>
        {isSubmitting ? (
          <Icon path={mdiLoading} size={1.5} spin={1} />
        ) : isDesktop ? (
          "Next"
        ) : (
          "SIGNUP"
        )}
      </Button>

      <span className={styles.signinLink}>
        <Link {...(isDesktop ? { to: `${routes.SIGNIN}` } : { to: "" })} data-cy="signin-link">
          {!isDesktop ? "Have an account? " : "Need to Login? "}
        </Link>
        <Link to={routes.SIGNIN} className="mobile:underline" data-cy="signin-link">
          {!isDesktop ? "Signin" : "Click here"}
        </Link>
      </span>
    </form>
  );
}
